// convex/lib/config/domains/collections.ts

import { validatorCollectionRecord } from "../../../companies/collections";
import { collectionRecordsRule } from "../../../companies/collections/filter_rules";
import { validatorUsers } from "../../../system/users";
import type { DomainGroup, TableScope } from "../types";

/**
 * Colecciones propias (tablas creadas por el usuario). El módulo
 * `collection_records` es el techo de permisos de los REGISTROS de todas las
 * colecciones; cada colección puede además restringirse a ciertos roles
 * (`collections.allowedRoleIds`). La navegación es dinámica (un ítem por
 * colección en el sidebar), por eso el módulo no aparece en el menú estático.
 * La configuración de colecciones vive en el módulo `custom_fields`.
 */
export const collectionsDomain: DomainGroup = {
  id: "collections",
  name: "Colecciones",
  description: "Tablas propias de la organización",
  icon: "Database",
  order: 24,
  status: "active",
  // Solo navegación: el módulo `collection_records` se re-parenta
  // bajo el grupo "configuration" en el sidebar de Settings, para
  // que `/settings/catalogs` aparezca como ítem propio dentro del
  // menú de Configuración (junto a `custom_fields` con su
  // `/settings/collections`). Permisos y tablas siguen radicadas
  // en este dominio.
  navParentGroup: "configuration",

  i18n: {
    namespace: "collections",
    keys: {
      title: "collections.title",
      description: "collections.description",
    },
  },

  modules: [
    {
      id: "collection_records",
      // El sidebar de Settings (components/settings/menu-sidebar.tsx)
      // pinta `module.name` literal, sin pasar por la i18n. Por eso
      // el `name` del módulo es el label que termina en el menú de
      // Configuración. La entry en `lib/locales/*/navigation.json`
      // (`modules.collection_records = "Catálogos"`) sigue siendo
      // la traducción para el sidebar principal, donde el label sí
      // viaja por i18n.
      name: "Catálogos",
      description: "Ver y cargar registros de las colecciones propias",
      icon: "Tag",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      // Dos rutas según el contexto:
      // - `/collections` → navegación principal (records de cada
      //   colección propia, vía `useCollectionsNavGroup`).
      // - `/settings/catalogs` → item del sidebar de Settings.
      // La posición del array define la ruta primaria, por eso
      // `/collections` se mantiene primera.
      routes: ["/collections", "/settings/catalogs"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 5,
        status: "active",
      },
      i18n: {
        namespace: "collections.records",
        keys: {
          title: "collections.records.title",
          description: "collections.records.description",
          longDescription: "collections.records.longDescription",
          searchPlaceholder: "collections.records.searchPlaceholder",
          emptyState: "collections.records.emptyState",
          actions: {
            create: "collections.records.actions.create",
            edit: "collections.records.actions.edit",
            delete: "collections.records.actions.delete",
            view: "collections.records.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },
      tables: [
        {
          tableName: "collection_records",
          schema: validatorCollectionRecord,
          scope: "company" as TableScope,
          // Los registros de las colecciones del usuario respetan el flag
          // `masterDataScope` de la empresa: si la empresa tiene scope "branch"
          // y el usuario no es super_admin, los registros se filtran por
          // `internalControl.branchId`. Con "company" (default) el
          // comportamiento es idéntico al actual.
          masterData: true,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["searchText"],
          hasStatusIndex: false,
          // Exige `collectionId` y acceso a la colección (roles).
          customRules: [collectionRecordsRule],
        },
      ],
      readOnlyTables: [],
    },
  ],
};
